import React, { useRef } from 'react'
import { Card, Col, Input, List, Typography, Row } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { IntegrationType } from 'src/types'

import { ApplicationState } from 'src/store'
import { currencyMask } from 'src/components/Formik'
import { HotelImageUrl } from 'src/views/Requests/views/Form/components/Steps/Services/components/Hotels/components/utils'
import { setFullRequest } from 'src/core/store/ducks/requests/actions'
import { TagTicketSituation } from 'src/components/molecules'
import { Text, Title } from 'src/components/Ant'
import UserAvatar from 'src/components/UserAvatar'

import { SelectedHotel } from '.'
import { UpdateETicketCodeRequest } from '../util'
import { OfflineOffer } from './styles'

type Props = {
  item: SelectedHotel
  canEdit: boolean
}

function TicketItem({ canEdit, item }: Props): JSX.Element {
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const refTimeout = useRef<NodeJS.Timeout>(null)
  const situation = item?.situation
  const { request_standard_sharings } =
    requests.fullRequest?.request_standard_assignments

  return (
    <List.Item key={item.request_quote_hotel_room_hash}>
      <Card
        title={
          <Row gutter={24}>
            <Col
              span={8}
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              <Text type="secondary">
                <Text strong type="secondary">
                  Check-in:
                </Text>{' '}
                {item.checkin_date_formatted}
              </Text>
              <Text type="secondary">
                <Text strong type="secondary">
                  Check-out:
                </Text>{' '}
                {item.checkout_date_formatted}
              </Text>
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {!!situation && <TagTicketSituation status={situation} />}
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {situation === 'ISSUED' && (
                <Text
                  code
                  copyable={{ text: item.eticket_code }}
                  style={{ fontSize: '16px' }}
                >
                  {item.eticket_code}
                </Text>
              )}
              {item.status === 'EMISSION_PENDING' && (
                <Input
                  disabled={!canEdit}
                  size="small"
                  placeholder="Emissão"
                  defaultValue={item.eticket_code}
                  onChange={({ target: { value } }) => {
                    clearTimeout(refTimeout.current)
                    refTimeout.current = setTimeout(() => {
                      dispatch(
                        setFullRequest(
                          UpdateETicketCodeRequest({
                            requests: requests.fullRequest,
                            request_ticket_id: item.request_ticket_id,
                            value
                          })
                        )
                      )
                    }, 500)
                  }}
                />
              )}
            </Col>
          </Row>
        }
      >
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                width: 200
              }}
            >
              <HotelImageUrl
                url={item.hotel_image_url}
                style={{ marginRight: 16, maxHeight: 200, maxWidth: 200 }}
              />
            </div>
            <div>
              <Typography.Title level={4}>{item.brand_name}</Typography.Title>
              <Typography.Text>{item.category}</Typography.Text>
              <br />
              <br />
              <Typography.Text>{item.address}</Typography.Text>
              <br />
              <br />
              <Typography.Text>
                {`- ${
                  item.pension
                    ? t('web.services.hotels.form.pension')
                    : t('web.services.hotels.form.no-pension')
                }`}
              </Typography.Text>
            </div>
          </div>
          {item.integration_type === IntegrationType.OFFLINE && (
            <Row style={{ marginTop: 16 }}>
              <Col>
                <OfflineOffer>
                  {t('web.services.hotels.offline-offer')}
                </OfflineOffer>
              </Col>
            </Row>
          )}
          {item.request_ticket_user_data.map(passenger => (
            <Row key={passenger.traveler.user_id} style={{ marginTop: 16 }}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <UserAvatar
                  gender={passenger.traveler.gender}
                  name={passenger.traveler.name}
                  size="large"
                  style={{ marginRight: 8 }}
                />
                <Title level={4} style={{ marginBottom: 0 }}>
                  {passenger.traveler.name}
                </Title>
                <Text style={{ marginBottom: 0 }}>
                  <b>{`${t('web.user.common.birthdate.title')}: `}</b>
                  {moment
                    .unix(passenger.traveler.birthdate)
                    .utc()
                    .format('DD/MM/YYYY')}
                </Text>
                <Text style={{ marginBottom: 0 }}>
                  <b>{`${t('web.user.common.vat_number.title')}: `}</b>
                  {passenger.traveler.vat_number}
                </Text>
              </a>
            </Row>
          ))}
          {!!request_standard_sharings && (
            <Row style={{ marginTop: 16 }}>
              {request_standard_sharings
                ?.filter(({ active }) => active)
                .map(request_standard_sharing => (
                  <Text
                    key={request_standard_sharing.request_standard_sharing_id}
                  >
                    <b>{`${t('web.resources.cost-centers.title')}: `}</b>
                    {`${request_standard_sharing.cost_center.code} - ${request_standard_sharing.cost_center.name}`}
                  </Text>
                ))}
            </Row>
          )}
          <Row
            gutter={8}
            style={{
              border: '1px dashed #e8e8e8',
              borderRadius: 4,
              marginTop: 24,
              padding: '12px 16px'
            }}
          >
            <Col offset={20} span={4}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">Total:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: item.total_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
          </Row>
        </>
      </Card>
    </List.Item>
  )
}

export default TicketItem
